import { ChangeDetectorRef, Component, EventEmitter, Input, Output, input } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { emailMaxLengthValidator, notEmptyValidator, ValidationPatterns } from "@bbwt/modules/validation";
import { RoleService } from "@main/roles";
import { UserService } from "@main/users";
import { FormButtonsConfig } from "@mipro/components";
import { UserSuggestion } from "@project/models";
import { SharedFileService } from "@mipro/services/shared-file.service";
import { SelectItem } from "primeng/api";
import { ControlBase } from "./control-base";
import { ShareBehavior, SharedFile } from "./shared-file";
import { UserHelperService } from "@main/users/user-helper.service";

@Component({
    selector: "m-fileShare",
    templateUrl: "./file-share.control.html",
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FileShareControlComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FileShareControlComponent,
            multi: true
        }
    ]
})
export class FileShareControlComponent extends ControlBase<SharedFile> {
    private _visible = false;

    readonly skipAuthenticationCheck = input(true);
    @Input() header: string = "Share file";

    @Input()
    set visible(value: boolean) {
        if (this._visible !== value) {
            this._visible = value;
            this.visibleChange.emit(value);
        }
    }
    get visible() {
        return this._visible;
    }

    @Output() readonly visibleChange = new EventEmitter<boolean>();
    @Output() readonly back = new EventEmitter<SharedFile>();

    private _fileDetailsId: string;

    @Input()
    set fileDetailsId(value: string) {
        this._fileDetailsId = value;
        if (value) {
            this.shareFileForm.patchValue({ fileDetailsId: value });
        }
    }
    get fileDetailsId() {
        return this._fileDetailsId;
    }

    private _sharedFile: SharedFile;

    shareFileForm: UntypedFormGroup;
    sharingFile = false;

    shareFileFormButtonsConfig = <FormButtonsConfig>{
        cancelFunc: () => {
            this.visible = false;
            this.back?.emit(null);
        }
    };

    userTypeOptions = [];
    userSuggestions: UserSuggestion[] = [];

    get selectUserDisabled(): boolean {
        return !this.shareFileForm.get("userType").value;
    }

    constructor(
        private sharedFileService: SharedFileService,
        private userHelperService: UserHelperService,
        private userService: UserService,
        private roleService: RoleService,
        private _cd: ChangeDetectorRef,
        private fb: UntypedFormBuilder
    ) {
        super();

        this.initForm();
        this.initUserTypes();
    }

    private async initUserTypes() {
        this.userTypeOptions = this.roleService.currentOrganizationRoles.map(x => <SelectItem>{ label: x.name, value: x.id });
    }

    writeValue(sharedFile: SharedFile): void {
        this._sharedFile = sharedFile;
    }

    private initForm(): void {
        this.shareFileForm = this.fb.group({
            fileDetailsId: [this._fileDetailsId],
            userType: [null],
            user: [null],
            email: [null, [notEmptyValidator(), Validators.pattern(ValidationPatterns.email), emailMaxLengthValidator]]
        });
    }

    async filterUsers(searchTerm: string) {
        if (this.shareFileForm.get("userType").value) {
            await this.applyFilter(searchTerm, this.shareFileForm.get("userType").value);
        } else {
            this.filterUsersForAllRoles(searchTerm);
        }
    }

    async filterUsersByRole(searchTerm: string) {
        this.shareFileForm.patchValue({ user: null });
        await this.applyFilter(null, searchTerm);
    }

    private async applyFilter(name: string, role: string) {
        const users = await this.sharedFileService.getUsersPage([
            { searchTerm: name, field: "fullName" },
            { searchTerm: role, field: "roleId" },
            { searchTerm: this.userHelperService.hashedOrganizationId, field: "organizationId" }
        ]);
        this.userSuggestions = users.items;
        this._cd.detectChanges();
    }

    async filterUsersForAllRoles(name: string = null) {
        if (!this.userTypeOptions.length) {
            await this.initUserTypes();
        }

        let allUsers = [];

        for (const option of this.userTypeOptions) {
            const users = await this.sharedFileService.getUsersPage([
                { searchTerm: name, field: "fullName" },
                { searchTerm: option.value, field: "roleId" },
                { searchTerm: this.userHelperService.hashedOrganizationId, field: "organizationId" }
            ]);

            allUsers = allUsers.concat(users.items);
        }
        this.userSuggestions = allUsers.filter((user, index, self) => index === self.findIndex(t => t.id === user.id));

        this._cd.detectChanges();
    }

    async onSelectUser(user: UserSuggestion) {
        this.userService.getUserEmail(user.id).then(email => {
            this.shareFileForm.patchValue({ email: email });
            this._cd.detectChanges();
        });
    }
    async shareFile() {
        if (this.shareFileForm.invalid) {
            return;
        }

        this.sharingFile = true;

        try {
            const createdFile = await this.sharedFileService.create({
                ...this.shareFileForm.value,
                shareBehavior: ShareBehavior.AlertAndEmail,
                skipAuthenticationCheck: this.skipAuthenticationCheck()
            });

            if (createdFile && createdFile.id) {
                await this.sharedFileService.addAccessHistoryToFile(createdFile.id, true);
            }
            this.back?.emit(this.shareFileForm.value);
            this.visible = false;
        } catch (error) {
            /* Ignore error */
        } finally {
            this.sharingFile = false;
        }
    }

    onHide() {
        this.shareFileForm.reset({ fileDetailsId: this._fileDetailsId });
    }
}
